import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { SettingsModal } from '../components/SettingsModal';
import { AssignRoleModal } from '../components/AssignRoleModal';
import { UserPermissionOverridesPanel } from '../components/UserPermissionOverridesPanel';
import * as api from '../services/settingsApi';
import {
    Shield, Plus, Save, Pencil, Trash2, ArrowLeft,
    ShieldCheck, ShieldAlert, Key, Users,
    Eye, ChevronRight, X, CheckCircle2, UserCheck,
    Copy, Search, Lock, Info,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// PERMISSION CATEGORY METADATA
// ============================================================

interface CategoryMeta {
    label: string;
    icon: string;
    color: string;
    description: string;
}

const PERMISSION_CATEGORY_META: Record<string, CategoryMeta> = {
    'tenant': { label: 'Organization', icon: 'Building2', color: 'from-violet-500 to-purple-600', description: 'View and edit organization details' },
    'users': { label: 'Staff Management', icon: 'Users', color: 'from-purple-500 to-violet-600', description: 'Create and manage staff accounts' },
    'roles': { label: 'Roles & Permissions', icon: 'Shield', color: 'from-amber-500 to-orange-600', description: 'Define what each role can do' },
    'products': { label: 'Product Catalog', icon: 'Package', color: 'from-emerald-500 to-teal-600', description: 'Manage products and pricing' },
    'categories': { label: 'Categories', icon: 'FolderTree', color: 'from-green-500 to-emerald-600', description: 'Organize products into categories' },
    'inventory': { label: 'Inventory & Stock', icon: 'ClipboardCheck', color: 'from-blue-500 to-indigo-600', description: 'Track stock levels' },
    'stock': { label: 'Stock Operations', icon: 'Boxes', color: 'from-sky-500 to-blue-600', description: 'Stock adjustments and transfers' },
    'procurement': { label: 'Purchasing', icon: 'Truck', color: 'from-cyan-500 to-sky-600', description: 'Purchase orders and suppliers' },
    'sales': { label: 'Sales & POS', icon: 'ShoppingCart', color: 'from-rose-500 to-pink-600', description: 'Process sales and returns' },
    'pos': { label: 'POS Terminal', icon: 'Monitor', color: 'from-red-500 to-rose-600', description: 'POS access and shifts' },
    'customers': { label: 'Customers', icon: 'UserCheck', color: 'from-green-500 to-emerald-600', description: 'Customer records' },
    'reports': { label: 'Reports', icon: 'BarChart3', color: 'from-indigo-500 to-blue-600', description: 'Business reports' },
    'audit': { label: 'Audit Logs', icon: 'Search', color: 'from-slate-500 to-slate-700', description: 'Activity history' },
    'settings': { label: 'Settings', icon: 'Settings', color: 'from-zinc-500 to-zinc-700', description: 'System configuration' },
    'hampers': { label: 'Hampers', icon: 'Gift', color: 'from-pink-500 to-rose-600', description: 'Product bundles' },
    'invoices': { label: 'Invoices', icon: 'FileText', color: 'from-yellow-500 to-amber-600', description: 'Invoices and payments' },
};

interface Role {
    id: string;
    name: string;
    description: string | null;
    isSystemRole: boolean;
    permissions?: { id: string; module: string; action: string }[];
}

interface Permission {
    id: string;
    module: string;
    action: string;
    description: string | null;
}

export function RolesPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const hasPermission = useAuthStore((s) => s.hasPermission);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showForm, setShowForm] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [selectedPerms, setSelectedPerms] = useState<string[]>([]);
    const [viewingRole, setViewingRole] = useState<Role | null>(null);
    const [form, setForm] = useState({ name: '', description: '' });
    const [searchPerms, setSearchPerms] = useState('');

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [overridePanel, setOverridePanel] = useState<{
        userId: string;
        userName: string;
        userEmail: string;
    } | null>(null);

    // ✅ Use the new endpoint that includes permissions
    const { data: roles, isLoading } = useQuery<Role[]>({
        queryKey: ['roles-with-permissions', user?.tenantId],
        queryFn: () => api.getRolesWithPermissions(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const { data: permissions, isLoading: permissionsLoading, error: permissionsError } = useQuery<Permission[]>({
        queryKey: ['permissions'],
        queryFn: () => api.getPermissions(),
        enabled: !!user?.tenantId,
    });

    // Show error toast if permissions fail to load
    useEffect(() => {
        if (permissionsError) {
            addToast({
                type: 'error',
                title: 'Failed to load permissions',
                message: 'Please try refreshing the page.',
            });
        }
    }, [permissionsError]);

    const saveMutation = useMutation({
        mutationFn: (payload: any) =>
            editingRole
                ? api.updateRole(user!.tenantId, editingRole.id, payload)
                : api.createRole(user!.tenantId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles-with-permissions'] });
            addToast({
                type: 'success',
                title: editingRole ? 'Role updated' : 'Role created',
            });
            closeForm();
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Failed',
                message: err?.response?.data?.error?.message,
            }),
    });

    const deleteMutation = useMutation({
        mutationFn: (roleId: string) => api.deleteRole(user!.tenantId, roleId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles-with-permissions'] });
            addToast({ type: 'success', title: 'Role deleted' });
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Failed',
                message: err?.response?.data?.error?.message,
            }),
    });

    const closeForm = () => {
        setShowForm(false);
        setEditingRole(null);
        setForm({ name: '', description: '' });
        setSelectedPerms([]);
        setSearchPerms('');
    };

    const handleEdit = (role: Role) => {
        if (role.isSystemRole) {
            addToast({
                type: 'warning',
                title: 'System Role Protected',
                message: 'Predefined system roles cannot be modified directly. Use "Clone as Custom Role" to create a customizable copy.',
            });
            return;
        }
        setEditingRole(role);
        setForm({ name: role.name, description: role.description || '' });
        setSelectedPerms(role.permissions?.map((p) => p.id) || []);
        setSearchPerms('');
        setShowForm(true);
    };

    const handleCloneRole = (role: Role) => {
        setEditingRole(null);
        setForm({
            name: `${role.name} (Custom)`,
            description: `Customized derivative of the standard ${role.name} role`,
        });
        setSelectedPerms(role.permissions?.map((p) => p.id) || []);
        setSearchPerms('');
        setShowForm(true);
        addToast({
            type: 'info',
            title: 'Role Cloned for Customization',
            message: `Permissions from ${role.name} have been copied. Adjust them and save your custom role.`,
        });
    };

    const togglePerm = (id: string) => {
        setSelectedPerms((prev) =>
            prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
        );
    };

    const selectAllInModule = (module: string) => {
        const modulePermIds = (permissions || [])
            .filter((p) => p.module === module)
            .map((p) => p.id);
        const allSelected = modulePermIds.every((id) => selectedPerms.includes(id));
        if (allSelected) {
            setSelectedPerms((prev) =>
                prev.filter((id) => !modulePermIds.includes(id))
            );
        } else {
            setSelectedPerms((prev) => [...new Set([...prev, ...modulePermIds])]);
        }
    };

    // Group permissions by module with search filtering
    const filteredPermissions = (permissions || []).filter((p) => {
        if (!searchPerms.trim()) return true;
        const q = searchPerms.toLowerCase();
        return (
            p.action.toLowerCase().includes(q) ||
            p.module.toLowerCase().includes(q) ||
            (p.description && p.description.toLowerCase().includes(q))
        );
    });

    const groupedPermissions: Record<string, Permission[]> = {};
    for (const perm of filteredPermissions) {
        if (!groupedPermissions[perm.module]) {
            groupedPermissions[perm.module] = [];
        }
        groupedPermissions[perm.module].push(perm);
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    className="w-14 h-14 rounded-2xl border-4 border-amber-200 border-t-amber-600"
                />
            </div>
        );
    }

    const customRoles = (roles || []).filter((r) => !r.isSystemRole);
    const systemRoles = (roles || []).filter((r) => r.isSystemRole);

    const canManageRoles = hasPermission('roles:create') || hasPermission('roles:edit') || hasPermission('roles:delete');
    const canAssignRoles = hasPermission('roles:assign');
    const canViewRoles = hasPermission('roles:view');

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Header */}
                <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 px-6 lg:px-8 py-4 sticky top-0 z-30 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/settings')}
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg shadow-amber-500/25">
                                <Shield className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Roles & Permissions
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {roles?.length || 0} roles · {customRoles.length} custom ·{' '}
                                    {systemRoles.length} system
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {canAssignRoles && (
                                <button
                                    onClick={() => setShowAssignModal(true)}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-amber-200 text-amber-700 rounded-xl hover:bg-amber-50 transition-all font-semibold text-sm"
                                    type="button"
                                >
                                    <Users className="w-4 h-4" /> Assign Roles
                                </button>
                            )}
                            {canManageRoles && (
                                <button
                                    onClick={() => {
                                        closeForm();
                                        setShowForm(true);
                                    }}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all font-semibold shadow-lg shadow-amber-500/25 text-sm"
                                    type="button"
                                >
                                    <Plus className="w-4 h-4" /> Create Role
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* System Roles */}
                <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <ShieldAlert size={16} /> System Roles (Built-in)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {systemRoles.map((role) => (
                            <motion.div
                                key={role.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                whileHover={{ y: -3 }}
                                className="bg-white rounded-2xl border-2 border-blue-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
                                onClick={() => setViewingRole(viewingRole?.id === role.id ? null : role)}
                            >
                                <div className="h-1 bg-gradient-to-r from-blue-500 to-indigo-600" />
                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                                                <ShieldAlert size={20} className="text-white" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-slate-900">
                                                    {role.name}
                                                </h4>
                                                <p className="text-xs text-slate-500">
                                                    {role.description || 'Built-in system role'}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge variant="info" size="sm">
                                            System
                                        </Badge>
                                    </div>
                                    <div className="flex items-center justify-between pt-2.5 mt-2 border-t border-slate-100">
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                            <Lock size={12} className="text-blue-500" />
                                            <span className="font-medium text-slate-600">Locked System Role</span>
                                        </div>
                                        {canManageRoles && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCloneRole(role);
                                                }}
                                                className="px-2.5 py-1 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition border border-blue-200/60 inline-flex items-center gap-1"
                                                type="button"
                                                title="Clone this system role to customize its permissions"
                                            >
                                                <Copy size={12} />
                                                Clone as Custom Role
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Custom Roles */}
                {customRoles.length > 0 && (
                    <div>
                        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <ShieldCheck size={16} /> Custom Roles
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {customRoles.map((role) => (
                                <motion.div
                                    key={role.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    whileHover={{ y: -3 }}
                                    className="bg-white rounded-2xl border-2 border-amber-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer"
                                    onClick={() => setViewingRole(viewingRole?.id === role.id ? null : role)}
                                >
                                    <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-600" />
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
                                                    <ShieldCheck size={20} className="text-white" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-slate-900">
                                                        {role.name}
                                                    </h4>
                                                    <p className="text-xs text-slate-500">
                                                        {role.description || 'Custom role'}
                                                    </p>
                                                </div>
                                            </div>
                                            {canManageRoles && (
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCloneRole(role);
                                                        }}
                                                        className="p-1.5 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600"
                                                        type="button"
                                                        title="Duplicate role"
                                                    >
                                                        <Copy size={14} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEdit(role);
                                                        }}
                                                        className="p-1.5 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600"
                                                        type="button"
                                                        title="Edit role"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (confirm(`Delete the "${role.name}" role?`)) {
                                                                deleteMutation.mutate(role.id);
                                                            }
                                                        }}
                                                        className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500"
                                                        type="button"
                                                        title="Delete role"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                                            <Eye size={12} /> {role.permissions?.length || 0} permissions
                                            <ChevronRight size={12} />
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {(!roles || roles.length === 0) && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-20 bg-white rounded-3xl border border-slate-200"
                    >
                        <div className="w-20 h-20 bg-amber-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <Shield className="w-10 h-10 text-amber-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700">
                            No roles configured
                        </h3>
                        <p className="text-slate-400 mt-2 max-w-md mx-auto">
                            Create custom roles to control what each staff member can access in the system.
                        </p>
                        {canManageRoles && (
                            <button
                                onClick={() => {
                                    closeForm();
                                    setShowForm(true);
                                }}
                                className="mt-6 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all font-semibold shadow-lg"
                                type="button"
                            >
                                <Plus className="w-5 h-5 inline mr-2" /> Create First Role
                            </button>
                        )}
                    </motion.div>
                )}

                {/* Viewing Role Permissions */}
                <AnimatePresence>
                    {viewingRole && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900">
                                            {viewingRole.name} — Permissions
                                        </h3>
                                        <p className="text-sm text-slate-500">
                                            {viewingRole.permissions?.length || 0} permissions assigned
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setViewingRole(null)}
                                        className="p-2 rounded-lg hover:bg-slate-100"
                                        type="button"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {Object.entries(PERMISSION_CATEGORY_META).map(([module, cat]) => {
                                        const rolePerms = viewingRole.permissions?.filter((p) => p.module === module) || [];
                                        return (
                                            <div key={module} className="p-4 bg-slate-50 rounded-xl">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className={`p-1.5 rounded-lg bg-gradient-to-br ${cat.color}`}>
                                                        <Shield size={14} className="text-white" />
                                                    </div>
                                                    <span className="text-sm font-semibold text-slate-700">
                                                        {cat.label}
                                                    </span>
                                                </div>
                                                {rolePerms.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {rolePerms.map((p) => (
                                                            <Badge key={p.id} variant="success" size="sm">
                                                                {p.action}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-slate-400">No access</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Create/Edit Role Modal */}
                <SettingsModal
                    open={showForm}
                    onClose={closeForm}
                    title={editingRole ? 'Edit Role' : 'Create New Role'}
                    subtitle="Define what this role can access in the system"
                    icon={Shield}
                    size="lg"
                    footer={
                        <>
                            <Button variant="secondary" onClick={closeForm}>
                                Cancel
                            </Button>
                            <Button
                                leftIcon={<Save size={16} />}
                                onClick={() =>
                                    saveMutation.mutate({
                                        name: form.name,
                                        description: form.description,
                                        permissionIds: selectedPerms,
                                    })
                                }
                                isLoading={saveMutation.isPending}
                                disabled={!form.name || selectedPerms.length === 0}
                            >
                                {editingRole ? 'Update Role' : 'Create Role'}
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <Input
                            label="Role Name *"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="e.g., Branch Manager, Senior Cashier"
                            required
                        />
                        <Input
                            label="Description"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Brief description of this role's responsibilities"
                        />

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    Permissions ({selectedPerms.length} of {permissions?.length || 0} selected)
                                </label>
                                <button
                                    onClick={() =>
                                        setSelectedPerms(
                                            selectedPerms.length > 0
                                                ? []
                                                : (permissions || []).map((p) => p.id)
                                        )
                                    }
                                    className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                                    type="button"
                                >
                                    {selectedPerms.length > 0 ? 'Clear All' : 'Select All'}
                                </button>
                            </div>

                            <div className="relative mb-3">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Filter permissions (e.g., pos, sales, inventory, barcode, reports)..."
                                    value={searchPerms}
                                    onChange={(e) => setSearchPerms(e.target.value)}
                                    className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 focus:bg-white transition-all"
                                />
                                {searchPerms && (
                                    <button
                                        onClick={() => setSearchPerms('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        type="button"
                                    >
                                        <X size={13} />
                                    </button>
                                )}
                            </div>

                            {permissionsLoading ? (
                                <div className="text-center py-8">
                                    <Spinner size="md" />
                                    <p className="text-sm text-slate-500 mt-2">Loading permissions...</p>
                                </div>
                            ) : permissionsError ? (
                                <div className="text-center py-8 text-rose-500">
                                    Failed to load permissions. Please try again.
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                                    {Object.entries(groupedPermissions).map(([module, perms]) => {
                                        const cat = PERMISSION_CATEGORY_META[module] || {
                                            label: module,
                                            color: 'from-slate-400 to-slate-500',
                                            description: '',
                                        };
                                        const selectedInModule = perms.filter((p) => selectedPerms.includes(p.id)).length;
                                        const allSelected = perms.length > 0 && selectedInModule === perms.length;

                                        return (
                                            <div key={module} className="border border-slate-200 rounded-xl overflow-hidden">
                                                <button
                                                    type="button"
                                                    onClick={() => selectAllInModule(module)}
                                                    className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`p-1.5 rounded-lg bg-gradient-to-br ${cat.color}`}>
                                                            <Shield size={16} className="text-white" />
                                                        </div>
                                                        <div className="text-left">
                                                            <p className="text-sm font-semibold text-slate-700">{cat.label}</p>
                                                            <p className="text-[10px] text-slate-400">{cat.description}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-slate-400">
                                                            {selectedInModule}/{perms.length}
                                                        </span>
                                                        <div
                                                            className={clsx(
                                                                'w-4 h-4 rounded border-2 flex items-center justify-center transition-all',
                                                                allSelected ? 'bg-amber-500 border-amber-500' : 'border-slate-300'
                                                            )}
                                                        >
                                                            {allSelected && <CheckCircle2 size={12} className="text-white" />}
                                                        </div>
                                                    </div>
                                                </button>
                                                <div className="px-3 pb-3 space-y-1">
                                                    {perms.map((perm) => (
                                                        <label
                                                            key={perm.id}
                                                            className={clsx(
                                                                'flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm transition-colors',
                                                                selectedPerms.includes(perm.id)
                                                                    ? 'bg-amber-50 border border-amber-200'
                                                                    : 'hover:bg-slate-50 border border-transparent'
                                                            )}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedPerms.includes(perm.id)}
                                                                onChange={() => togglePerm(perm.id)}
                                                                className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                                            />
                                                            <div>
                                                                <span className="text-slate-700 capitalize font-medium">
                                                                    {perm.action.replace(/_/g, ' ')}
                                                                </span>
                                                                <span className="text-slate-400 text-xs ml-2">
                                                                    — {perm.description || 'Access this feature'}
                                                                </span>
                                                            </div>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </SettingsModal>
            </div>

            {/* Assign Role Modal */}
            <AssignRoleModal
                open={showAssignModal}
                onClose={() => setShowAssignModal(false)}
            />

            {/* User Permission Overrides Panel */}
            {overridePanel && (
                <UserPermissionOverridesPanel
                    open={!!overridePanel}
                    onClose={() => setOverridePanel(null)}
                    userId={overridePanel.userId}
                    userName={overridePanel.userName}
                    userEmail={overridePanel.userEmail}
                />
            )}
        </div>
    );
}